/**
 * Style System Utilities
 *
 * Utilities for creating custom component styles with multi-part components.
 */
import type { ConditionalValue } from '@chakra-ui/react'

export interface StyleThemingProps {
  variant?: string
  size?: string | ConditionalValue<string>
  colorScheme?: string
  [key: string]: any
}

// Alias for backward compatibility
export type ThemingProps = StyleThemingProps

export type MultiStyleType<T extends readonly string[]> = Record<
  T[number],
  Record<string, any>
>

// ==============================================================================
// STYLE MERGING UTILITIES
// These functions implement the style lookup and merging logic that was
// previously provided by Chakra v2's theme system.
// ==============================================================================

/**
 * Deep merge utility for style objects
 */
function deepMerge<T extends Record<string, any>>(
  target: T,
  ...sources: Array<Partial<T> | undefined>
): T {
  if (!sources.length) return target
  const source = sources.shift()

  if (source === undefined) {
    return deepMerge(target, ...sources)
  }

  for (const key in source) {
    const sourceValue = source[key]
    const targetValue = target[key]

    if (
      sourceValue &&
      typeof sourceValue === 'object' &&
      !Array.isArray(sourceValue) &&
      targetValue &&
      typeof targetValue === 'object' &&
      !Array.isArray(targetValue)
    ) {
      target[key] = deepMerge({ ...targetValue }, sourceValue as any)
    } else if (sourceValue !== undefined) {
      target[key] = sourceValue as any
    }
  }

  return deepMerge(target, ...sources)
}

interface MultiStyleConfig {
  baseStyle?: Record<string, any> | ((props?: any) => Record<string, any>)
  sizes?: Record<string, Record<string, any>>
  variants?: Record<string, Record<string, any>>
  defaultProps?: StyleThemingProps
}

/**
 * Helper to check if a value is a responsive object (not array, not string)
 */
function isResponsiveObject(value: any): value is Record<string, string> {
  return (
    value &&
    typeof value === 'object' &&
    !Array.isArray(value) &&
    (value.base !== undefined ||
      Object.keys(value).some((k) =>
        ['sm', 'md', 'lg', 'xl', '2xl'].includes(k),
      ))
  )
}

/**
 * Merges responsive style objects
 * Takes styles from multiple breakpoints and combines them into a single responsive object
 */
function mergeResponsiveStyles(
  baseStyles: Record<string, any>,
  responsiveSizeOrVariant: Record<string, string>,
  configMap: Record<string, Record<string, any>>,
): Record<string, any> {
  const result = { ...baseStyles }

  // For each part in the base styles
  for (const part in result) {
    const partResult: Record<string, any> = { ...result[part] }

    // For each breakpoint in the responsive value
    for (const breakpoint in responsiveSizeOrVariant) {
      const configKey = responsiveSizeOrVariant[breakpoint]
      const configStyles = configMap[configKey]

      if (configStyles && configStyles[part]) {
        // For each property in this part's config
        for (const prop in configStyles[part]) {
          const value = configStyles[part][prop]

          // If this is the base breakpoint, set directly
          if (breakpoint === 'base') {
            partResult[prop] = value
          } else {
            // For other breakpoints, create responsive object
            if (
              typeof partResult[prop] === 'object' &&
              !Array.isArray(partResult[prop])
            ) {
              // Already a responsive object, add this breakpoint
              partResult[prop] = { ...partResult[prop], [breakpoint]: value }
            } else {
              // Convert to responsive object
              const baseValue = partResult[prop]
              partResult[prop] = { base: baseValue, [breakpoint]: value }
            }
          }
        }
      }
    }

    result[part] = partResult
  }

  return result
}

/**
 * Merges base styles with size and variant styles
 * Handles both simple values and responsive objects/arrays
 */
export function mergeMultiStyleConfig(
  styleConfig: MultiStyleConfig,
  props?: StyleThemingProps,
): Record<string, any> {
  const {
    baseStyle = {},
    sizes = {},
    variants = {},
    defaultProps = {},
  } = styleConfig
  const { size = defaultProps.size, variant = defaultProps.variant } =
    props || {}

  // Start with base styles - call function if needed
  let mergedStyles =
    typeof baseStyle === 'function' ? { ...baseStyle(props) } : { ...baseStyle }

  // Handle size styles
  if (size) {
    if (typeof size === 'string') {
      // Simple string value
      if (sizes[size]) {
        mergedStyles = deepMerge(mergedStyles, sizes[size])
      }
    } else if (isResponsiveObject(size)) {
      // Responsive object: {base: 'sm', xl: 'md'}
      mergedStyles = mergeResponsiveStyles(mergedStyles, size, sizes)
    } else if (Array.isArray(size)) {
      // Array: ['sm', 'md', 'lg'] -> use first non-null value
      const firstSize = size.find((v) => v != null)
      if (firstSize && sizes[firstSize]) {
        mergedStyles = deepMerge(mergedStyles, sizes[firstSize])
      }
    }
  }

  // Handle variant styles
  if (variant) {
    if (typeof variant === 'string') {
      // Simple string value
      if (variants[variant]) {
        mergedStyles = deepMerge(mergedStyles, variants[variant])
      }
    } else if (isResponsiveObject(variant)) {
      // Responsive object: {base: 'primary', lg: 'gray.solid'}
      mergedStyles = mergeResponsiveStyles(mergedStyles, variant, variants)
    } else if (Array.isArray(variant)) {
      // Array: ['primary', 'gray.solid'] -> use first non-null value
      const variantArray = variant as string[]
      const firstVariant = variantArray.find((v) => v != null)
      if (firstVariant && variants[firstVariant]) {
        mergedStyles = deepMerge(mergedStyles, variants[firstVariant])
      }
    }
  }

  return mergedStyles
}

// ==============================================================================
// DEPRECATED COMPATIBILITY HELPERS
// ==============================================================================

/**
 * @deprecated This function just returns an object with the parts array.
 * The returned helper functions (defineMultiStyleConfig, definePartsStyle) are
 * also identity functions.
 *
 * Before: const helpers = createMultiStyleConfigHelpers(parts)
 *         export const Foo = helpers.defineMultiStyleConfig({ ... })
 * After:  export const Foo = { ... }
 */
export function createMultiStyleConfigHelpers<T extends readonly string[]>(
  parts: T,
) {
  return {
    parts,
    defineMultiStyleConfig: <C>(config: C): C => config,
    definePartsStyle: <C>(config: C): C => config,
  }
}

/**
 * Pure function for applying multi-part component styles with size/variant support
 *
 * @deprecated Use mergeMultiStyleConfig directly instead
 * @param styleConfigOrKey - Style config object or legacy theme key string
 * @param props - Style theming props (size, variant, colorScheme, etc.)
 * @returns Merged styles for all component parts
 *
 * Usage:
 *   const styles = getMultiStyleConfig(CallButtonStyle, { size: 'md', variant: 'brand.solid' })
 *   <Box css={styles.button}>...</Box>
 */
export function getMultiStyleConfig<_T = any>(
  styleConfigOrKey: MultiStyleConfig | string,
  props?: StyleThemingProps,
): Record<string, any> {
  // If it's a string (legacy themeKey), we can't look it up, so return empty
  // This maintains backward compatibility but logs a warning
  if (typeof styleConfigOrKey === 'string') {
    console.warn(
      `getMultiStyleConfig: String keys are no longer supported. ` +
        `Pass the style config object directly instead of "${styleConfigOrKey}".`,
    )
    return props || {}
  }

  return mergeMultiStyleConfig(styleConfigOrKey, props)
}

/**
 * @deprecated This is no longer a hook. Use mergeMultiStyleConfig directly with useMemo if needed.
 * Kept for backwards compatibility.
 */
export const useMultiStyleConfig = getMultiStyleConfig
export const useCustomMultiStyleConfig = getMultiStyleConfig
